import { RefObject, useEffect, useRef } from 'react';
import {
  COLLAPSE_MS,
  COLORS,
  EVENT_HEIGHT,
  HEADER_HEIGHT,
  ROW_HEIGHT,
  STATUS_BAND_DIM_ALPHA,
  STATUS_STRIPE,
  STATUS_STRIPE_WIDTH,
  TILE_EDGE_PAD,
  TILE_HOVER_LIFT,
} from '../constants';
import { drawBookingTile } from '../renderers/bookingTileRenderer';
import { Booking, Tile } from '../utils/bookingTiles';
import { StatusPeriod } from '../utils/statusPeriods';

type DrawnRect = { tile: Tile; x: number; y: number; w: number; h: number };
// A clickable status band (planned change) — carries the originating booking.
type DrawnBand = {
  booking: Booking;
  x: number;
  y: number;
  w: number;
  h: number;
};

type Params = {
  canvasRef: RefObject<HTMLCanvasElement>;
  scrollRef: RefObject<HTMLDivElement>;
  // carId -> tiles for that car
  tilesByCar: Map<string, Tile[]>;
  // carId -> status-change period bands (grey/red) drawn behind the tiles.
  statusPeriodsByCar: Map<string, StatusPeriod[]>;
  // carId -> the live body row element (read for offsetTop/offsetHeight so tiles
  // follow the collapse CSS animation exactly — single source of truth = DOM).
  rowElRefs: RefObject<Map<string, HTMLDivElement | null>>;
  // changes when a group is collapsed/expanded → kicks the animation redraw loop
  collapseSignature: string;
  // changes when the row ORDER changes (sort / group / data) → tiles read Y from
  // live DOM geometry, so a reorder needs an explicit redraw (no data dep changes).
  rowOrderSignature: string;
  // bookingId/tripId of the open drawer — when set, every OTHER tile is greyed.
  selectedId: string | null;
  // total sticky-left width (plate + any extra columns) = where the day area starts.
  leftWidth: number;
  // px width of one column at the active zoom — used to redraw cell gridlines over bands.
  columnWidth: number;
  // day-local px of the current-time line (= (todayIdx+fraction)*DAY_WIDTH), or null.
  todayLineX: number | null;
  onTileClick: (tile: Tile) => void;
  // Click a PLANNED status-change band → open its booking (the band carries the booking).
  onBandClick?: (booking: Booking) => void;
  // Hover → full booking code + cursor pos for a DOM tooltip (null when off any tile).
  onHoverChange?: (
    info: { label: string; x: number; y: number } | null
  ) => void;
};

// Status-period candy-stripe CanvasPattern — alternating `STATUS_STRIPE_WIDTH` diagonal
// bands of `c1` (stripe) over a `c2` (background) fill, on a `width*2` tile. Verbatim
// geometry from ExampleTimeline/.../pending-pattern.ts `createPendingPattern`.
const createStripePattern = (
  ctx: CanvasRenderingContext2D,
  c1: string,
  c2: string
): CanvasPattern | null => {
  const sw = STATUS_STRIPE_WIDTH;
  const size = sw * 2;
  const tile = document.createElement('canvas');
  tile.width = size;
  tile.height = size;
  const tc = tile.getContext('2d');
  if (!tc) return null;
  tc.fillStyle = c2;
  tc.fillRect(0, 0, size, size);
  tc.fillStyle = c1;
  for (let offset = -size; offset < size * 2; offset += sw * 2) {
    tc.beginPath();
    tc.moveTo(offset, 0);
    tc.lineTo(offset + sw, 0);
    tc.lineTo(offset + sw + size, size);
    tc.lineTo(offset + size, size);
    tc.closePath();
    tc.fill();
  }
  return ctx.createPattern(tile, 'repeat');
};

// Canvas booking-tile layer. Viewport-sized canvas, translated by scroll each
// frame (a full-content canvas would blow past the 16384px limit). Tile Y comes
// from the live DOM row geometry so collapse/expand animation stays in sync.
const useBookingCanvas = ({
  canvasRef,
  scrollRef,
  tilesByCar,
  statusPeriodsByCar,
  rowElRefs,
  collapseSignature,
  rowOrderSignature,
  selectedId,
  leftWidth,
  columnWidth,
  todayLineX,
  onTileClick,
  onBandClick,
  onHoverChange,
}: Params): void => {
  const tilesRef = useRef(tilesByCar);
  tilesRef.current = tilesByCar;
  const onHoverChangeRef = useRef(onHoverChange);
  onHoverChangeRef.current = onHoverChange;
  const statusPeriodsRef = useRef(statusPeriodsByCar);
  statusPeriodsRef.current = statusPeriodsByCar;
  const onClickRef = useRef(onTileClick);
  onClickRef.current = onTileClick;
  const onBandClickRef = useRef(onBandClick);
  onBandClickRef.current = onBandClick;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const leftWidthRef = useRef(leftWidth);
  leftWidthRef.current = leftWidth;
  const columnWidthRef = useRef(columnWidth);
  columnWidthRef.current = columnWidth;
  const todayLineXRef = useRef(todayLineX);
  todayLineXRef.current = todayLineX;
  const drawnRectsRef = useRef<DrawnRect[]>([]);
  const drawnBandsRef = useRef<DrawnBand[]>([]);
  const rafRef = useRef(0);
  const scheduleRef = useRef<() => void>(() => {});
  // Hover highlight: the hovered tile id (static lift, no animation).
  const hoverIdRef = useRef<string | null>(null);
  // Lazily-built diagonal stripe CanvasPatterns for status periods (per ctx):
  // red for BLOCKED, grey for RELEASE_READY.
  const blockedPatternRef = useRef<CanvasPattern | null>(null);
  const releaseReadyPatternRef = useRef<CanvasPattern | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const scroller = scrollRef.current;
    if (!canvas || !scroller) return undefined;

    const draw = () => {
      rafRef.current = 0;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const cssW = scroller.clientWidth;
      const cssH = scroller.clientHeight;
      const bw = Math.round(cssW * dpr);
      const bh = Math.round(cssH * dpr);
      if (canvas.width !== bw) canvas.width = bw;
      if (canvas.height !== bh) canvas.height = bh;
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      // The sticky canvas is the last flow child; without this its box would add
      // its own height to the scroll content (blank space at the bottom). Negative
      // bottom margin collapses that flow contribution (same trick as ExampleTimeline).
      canvas.style.marginBottom = `-${cssH}px`;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, bw, bh);
      ctx.scale(dpr, dpr);

      // Clip to the body region (screen space) so tiles never paint under the
      // sticky left columns or header. `leftWidth` = plate + any extra columns.
      // MUST save/restore around the clip: `setTransform` resets the transform but
      // NOT the clip, and clip regions INTERSECT across frames — without a restore
      // the effective clip-left would be the max leftWidth ever seen, so removing a
      // column (shrinking leftWidth) could never reclaim the strip and the tile
      // there would stay clipped. The matching restore() is at the end of draw().
      const leftW = leftWidthRef.current;
      ctx.save();
      ctx.beginPath();
      ctx.rect(leftW, HEADER_HEIGHT, cssW - leftW, cssH - HEADER_HEIGHT);
      ctx.clip();

      const scrollLeft = scroller.scrollLeft;
      const scrollTop = scroller.scrollTop;
      ctx.translate(-scrollLeft, -scrollTop);

      // Inset both riding edges by TILE_EDGE_PAD so the sliding presentable-id
      // label keeps a gap from the left columns and the end-date label keeps a
      // gap from the viewport's right edge (neither sits flush against the edge).
      const visibleLeft = scrollLeft + leftW + TILE_EDGE_PAD;
      const visibleRight = scrollLeft + cssW - TILE_EDGE_PAD;

      const selId = selectedIdRef.current;
      const hoverId = hoverIdRef.current;
      const rects: DrawnRect[] = [];
      const bands: DrawnBand[] = [];
      const refs = rowElRefs.current;
      // The hovered tile is drawn LAST (on top, scaled) — defer it here.
      type Deferred = {
        t: Tile;
        x: number;
        y: number;
        w: number;
        h: number;
        alpha: number;
      };
      let hovered: Deferred | null = null;

      // --- Status-change period bands (drawn BEHIND the booking tiles, over the full
      // row height): soft fill + diagonal line (example style) — red for BLOCKED, slate
      // for RELEASE_READY. While a booking/trip drawer is open every band is dimmed
      // (STATUS_BAND_DIM_ALPHA) so the selected tile stands out. ---
      const periodsMap = statusPeriodsRef.current;
      if (periodsMap && periodsMap.size) {
        if (!blockedPatternRef.current) {
          blockedPatternRef.current = createStripePattern(
            ctx,
            STATUS_STRIPE.blocked.c1,
            STATUS_STRIPE.blocked.c2
          );
        }
        if (!releaseReadyPatternRef.current) {
          releaseReadyPatternRef.current = createStripePattern(
            ctx,
            STATUS_STRIPE.releaseReady.c1,
            STATUS_STRIPE.releaseReady.c2
          );
        }
        const blockedPattern = blockedPatternRef.current;
        const releaseReadyPattern = releaseReadyPatternRef.current;
        const colW = columnWidthRef.current;
        periodsMap.forEach((periods, carId) => {
          const rowEl = refs?.get(carId);
          if (!rowEl) return;
          const rowH = rowEl.offsetHeight;
          if (rowH <= 0) return;
          const rowY = HEADER_HEIGHT + rowEl.offsetTop;
          const rowAlpha = Math.min(1, rowH / ROW_HEIGHT);
          periods.forEach((p) => {
            const bandLeft = leftW + p.x;
            const bandRight = bandLeft + p.w;
            // Planned status-change bands are bookings → register a clickable rect.
            if (p.booking) {
              bands.push({
                booking: p.booking,
                x: bandLeft,
                y: rowY,
                w: p.w,
                h: rowH,
              });
            }
            // Candy-stripe fill (dimmed when a drawer is open — EXCEPT the band whose
            // booking is the open one, which stays fully highlighted like a tile).
            const isSel = selId != null && p.booking?.id === selId;
            const bandDim = selId != null && !isSel ? STATUS_BAND_DIM_ALPHA : 1;
            ctx.globalAlpha = rowAlpha * bandDim;
            ctx.fillStyle =
              p.status === 'BLOCKED'
                ? blockedPattern || STATUS_STRIPE.blocked.c2
                : releaseReadyPattern || STATUS_STRIPE.releaseReady.c2;
            ctx.fillRect(bandLeft, rowY, p.w, rowH);
            // Re-draw the cell gridlines OVER the opaque band so dividers stay
            // visible (the body's DOM gridlines are hidden underneath). Full
            // (un-dimmed) row alpha so borders stay crisp.
            if (colW > 0) {
              ctx.globalAlpha = rowAlpha;
              ctx.strokeStyle = COLORS.gridBorder;
              ctx.lineWidth = 1;
              ctx.beginPath();
              // row's top horizontal divider
              ctx.moveTo(bandLeft, rowY + 0.5);
              ctx.lineTo(bandRight, rowY + 0.5);
              // vertical column dividers at each column boundary within the band
              const firstK = Math.ceil((bandLeft - leftW) / colW);
              for (
                let gx = leftW + firstK * colW;
                gx <= bandRight;
                gx += colW
              ) {
                ctx.moveTo(gx + 0.5, rowY);
                ctx.lineTo(gx + 0.5, rowY + rowH);
              }
              ctx.stroke();
            }
          });
        });
        ctx.globalAlpha = 1;
      }

      // --- Current-time line: over the bands, under the tiles. Spans the body
      // height (max rendered row bottom); the body clip hides it behind the
      // sticky left columns when today scrolls past them. ---
      const tlx = todayLineXRef.current;
      if (tlx != null) {
        let bodyBottom = HEADER_HEIGHT;
        refs?.forEach((el) => {
          if (el) {
            const b = HEADER_HEIGHT + el.offsetTop + el.offsetHeight;
            if (b > bodyBottom) bodyBottom = b;
          }
        });
        ctx.fillStyle = COLORS.todayAccent;
        ctx.fillRect(leftW + tlx, HEADER_HEIGHT, 3, bodyBottom - HEADER_HEIGHT);
      }

      tilesRef.current.forEach((tiles, carId) => {
        const rowEl = refs?.get(carId);
        if (!rowEl) return;
        const rowH = rowEl.offsetHeight;
        if (rowH <= 0) return; // collapsed
        const rowTop = rowEl.offsetTop;
        const alpha = Math.min(1, rowH / ROW_HEIGHT);
        const tileH = Math.min(EVENT_HEIGHT, rowH);
        const y = HEADER_HEIGHT + rowTop + (rowH - tileH) / 2;
        tiles.forEach((t) => {
          const x = leftW + t.x;
          // Hit-testing always uses the un-scaled rect (stable on hover).
          rects.push({ tile: t, x, y, w: t.w, h: tileH });
          if (hoverId && t.bookingId === hoverId) {
            hovered = { t, x, y, w: t.w, h: tileH, alpha };
            return; // drawn after the loop, on top + scaled
          }
          // Grey out tiles that aren't the open one (and aren't being hovered).
          const dimmed =
            selId != null && t.bookingId !== selId && t.bookingId !== hoverId;
          drawBookingTile({
            ctx,
            x,
            y,
            w: t.w,
            h: tileH,
            bg: t.bg,
            textColor: t.textColor,
            label: t.label,
            endLabel: t.endLabel,
            visibleLeft,
            visibleRight,
            alpha,
            dimmed,
            isServiceTrip: t.isServiceTrip,
          });
        });
      });

      // Hovered tile last: a small STATIC upward lift + deeper shadow. A fixed
      // px lift (not a width-proportional scale) reads the same on short and long
      // bookings.
      if (hovered) {
        const d = hovered as Deferred;
        ctx.save();
        ctx.translate(0, -TILE_HOVER_LIFT);
        drawBookingTile({
          ctx,
          x: d.x,
          y: d.y,
          w: d.w,
          h: d.h,
          bg: d.t.bg,
          textColor: d.t.textColor,
          label: d.t.label,
          endLabel: d.t.endLabel,
          visibleLeft,
          visibleRight,
          alpha: d.alpha,
          // Hovered tile is never greyed — hovering a dimmed tile previews it.
          dimmed: false,
          elevated: true,
          isServiceTrip: d.t.isServiceTrip,
        });
        ctx.restore();
      }

      drawnRectsRef.current = rects;
      drawnBandsRef.current = bands;
      ctx.restore(); // pop the body-region clip so it doesn't intersect next frame
    };

    const scheduleRedraw = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(draw);
    };
    scheduleRef.current = scheduleRedraw;

    // Static hover highlight — just flip the hovered id and redraw once (no tween).
    const setHover = (id: string | null) => {
      if (id === hoverIdRef.current) return;
      hoverIdRef.current = id;
      scheduleRedraw();
    };

    const onScroll = () => scheduleRedraw();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(scheduleRedraw);
    ro.observe(scroller);

    const pointerContent = (
      e: MouseEvent
    ): { cx: number; cy: number } | null => {
      const rect = scroller.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      // Clicks/hovers over the sticky left columns (plate + extras) belong to the
      // PlateColumn that sits ON TOP of the canvas — NOT the tiles/bands scrolled
      // behind it. Without this guard a plate click maps to a content-x far to the
      // right and can spuriously hit a status band (the "click passes through" bug).
      if (localX < leftWidthRef.current) return null;
      return {
        cx: localX + scroller.scrollLeft,
        cy: e.clientY - rect.top + scroller.scrollTop,
      };
    };

    // Tiles take priority (they're drawn on top of the bands).
    const hitTest = (e: MouseEvent): Tile | null => {
      const pc = pointerContent(e);
      if (!pc) return null;
      const { cx, cy } = pc;
      const rects = drawnRectsRef.current;
      for (let i = rects.length - 1; i >= 0; i--) {
        const r = rects[i];
        if (cx >= r.x && cx <= r.x + r.w && cy >= r.y && cy <= r.y + r.h) {
          return r.tile;
        }
      }
      return null;
    };

    // Clickable PLANNED status-change bands (only reached when no tile is hit).
    const hitTestBand = (e: MouseEvent): Booking | null => {
      const pc = pointerContent(e);
      if (!pc) return null;
      const { cx, cy } = pc;
      const list = drawnBandsRef.current;
      for (let i = list.length - 1; i >= 0; i--) {
        const b = list[i];
        if (cx >= b.x && cx <= b.x + b.w && cy >= b.y && cy <= b.y + b.h) {
          return b.booking;
        }
      }
      return null;
    };

    const onClick = (e: MouseEvent) => {
      const tile = hitTest(e);
      if (tile) {
        onClickRef.current(tile);
        return;
      }
      const band = hitTestBand(e);
      if (band) onBandClickRef.current?.(band);
    };
    const onMove = (e: MouseEvent) => {
      const tile = hitTest(e);
      const band = tile ? null : hitTestBand(e);
      scroller.style.cursor = tile || band ? 'pointer' : '';
      setHover(tile ? tile.bookingId : null);
      // Full booking code + cursor pos for the hover tooltip (follows the cursor).
      onHoverChangeRef.current?.(
        tile ? { label: tile.label, x: e.clientX, y: e.clientY } : null
      );
    };
    const onLeave = () => {
      setHover(null);
      onHoverChangeRef.current?.(null);
    };
    scroller.addEventListener('click', onClick);
    scroller.addEventListener('mousemove', onMove);
    scroller.addEventListener('mouseleave', onLeave);

    scheduleRedraw();

    return () => {
      scroller.removeEventListener('scroll', onScroll);
      scroller.removeEventListener('click', onClick);
      scroller.removeEventListener('mousemove', onMove);
      scroller.removeEventListener('mouseleave', onLeave);
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef, scrollRef, rowElRefs]);

  // Redraw when tile/status data, the selected tile, left width, today line, or the
  // row ORDER change. The row-order signature covers sort/group reorders: tile Y is
  // read from live DOM geometry, and a reorder changes no data dep on its own — so
  // without this the canvas would only catch up on the next scroll.
  useEffect(() => {
    scheduleRef.current();
  }, [
    tilesByCar,
    statusPeriodsByCar,
    selectedId,
    leftWidth,
    columnWidth,
    todayLineX,
    rowOrderSignature,
  ]);

  // Collapse/expand: run a short redraw loop so tiles track the CSS height
  // animation frame-by-frame (reading live DOM geometry each frame).
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = () => {
      scheduleRef.current();
      if (performance.now() - start < COLLAPSE_MS + 60) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseSignature]);
};

export default useBookingCanvas;
